import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  UserCog, CheckCircle2, Trash2, ArrowRight, Phone, Mail, MessageCircle,
  MapPin, Calendar, User, FileText,
} from "lucide-react";

export default function ProfileUpdatesPage() {
  const { selectedBranch } = useBranch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(async () => {
    if (!selectedBranch?.id) { setRows([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/students/pending", {
        params: { branch_id: selectedBranch.id, submission_type: "profile_update" },
      });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selectedBranch?.id]);

  useEffect(() => { load(); }, [load]);

  const reject = async (id) => {
    if (!window.confirm("Reject and delete this profile update?")) return;
    try {
      await api.delete(`/students/pending/${id}`);
      toast.success("Profile update rejected");
      setRows((arr) => arr.filter((r) => r.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Profile Updates</h1>
          <p className="text-sm text-brand-inkMuted mt-1">
            Re-submissions from students already on file. Review the changes, then apply what you want to keep.
          </p>
          {selectedBranch?.name && (
            <p className="text-xs text-brand-inkMuted mt-1.5">
              Showing updates for <span className="font-semibold text-foreground">{selectedBranch.name}</span>. Switch branches via the header selector above.
            </p>
          )}
        </div>

        {loading ? (
          <p className="text-brand-inkMuted">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-3xl border border-brand-purple/10 p-10 text-center" data-testid="pu-empty">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-brand-purpleLight/40 flex items-center justify-center mb-3">
              <UserCog className="w-7 h-7 text-brand-purple" />
            </div>
            <p className="font-heading text-lg font-semibold text-foreground">No profile updates to review</p>
            <p className="text-sm text-brand-inkMuted mt-1">
              When someone already enrolled re-submits through the QR form, their submission lands here instead of Pending Enrollments.
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4" data-testid="pu-list">
            {rows.map((r) => (
              <UpdateCard key={r.id} row={r} onReview={() => setReviewing(r)} onReject={() => reject(r.id)} />
            ))}
          </div>
        )}
      </div>

      {reviewing && (
        <ReviewDialog
          row={reviewing}
          onClose={() => setReviewing(null)}
          onApproved={() => { setReviewing(null); toast.success("Student profile updated"); load(); }}
        />
      )}
    </>
  );
}

function UpdateCard({ row, onReview, onReject }) {
  const g0 = (row.guardians || [])[0] || {};
  const submitted = row.submitted_at ? new Date(row.submitted_at).toLocaleString() : "—";
  const s = row.existing_student || {};
  const diffs = useMemo(() => diffFields(s, row), [s, row]);
  return (
    <div className="bg-white rounded-3xl border border-brand-purple/10 p-5 sm:p-6" data-testid={`pu-card-${row.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <p className="font-heading text-lg font-bold text-foreground truncate">{row.full_name || row.name || "—"}</p>
          <p className="text-xs text-brand-inkMuted mt-0.5">{row.branch_name || "—"} · Submitted {submitted}</p>
        </div>
        <span className="rounded-full bg-[#EDE9FE] text-[#6D28D9] border border-[#DDD6FE] px-2.5 py-0.5 text-[10px] font-heading uppercase tracking-wider whitespace-nowrap">
          Profile Update
        </span>
      </div>

      {diffs.length === 0 ? (
        <p className="text-sm text-brand-inkMuted italic mb-4">Nothing new was submitted — you can safely reject.</p>
      ) : (
        <div className="space-y-2 mb-4" data-testid={`pu-diffs-${row.id}`}>
          {diffs.slice(0, 4).map((d) => (
            <div key={d.label} className="text-sm">
              <p className="text-[11px] font-heading uppercase tracking-wider text-brand-inkMuted flex items-center gap-1">
                {d.icon} {d.label}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-brand-inkMuted line-through truncate">{d.oldVal || "—"}</span>
                <ArrowRight className="w-3.5 h-3.5 text-brand-purple flex-shrink-0" />
                <span className="text-foreground font-medium truncate">{d.newVal || "—"}</span>
              </div>
            </div>
          ))}
          {diffs.length > 4 && (
            <p className="text-xs text-brand-inkMuted">+ {diffs.length - 4} more change{diffs.length - 4 === 1 ? "" : "s"}</p>
          )}
        </div>
      )}

      {g0.name && (
        <div className="rounded-2xl bg-brand-purpleLight/20 border border-brand-purple/10 p-3 mb-4 text-xs">
          <p className="font-heading text-brand-purple mb-1">Guardian (from submission)</p>
          <p className="text-foreground font-medium">{g0.name}</p>
          <div className="flex flex-wrap gap-3 text-brand-inkMuted mt-1">
            {g0.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{g0.phone}</span>}
            {g0.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{g0.email}</span>}
            {g0.fb_messenger && <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{g0.fb_messenger}</span>}
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={onReject} className="rounded-full h-10 px-4 border-[#FDECEA] text-[#C62828] hover:bg-[#FDECEA]" data-testid={`pu-reject-${row.id}`}>
          <Trash2 className="w-4 h-4 mr-1.5" /> Reject
        </Button>
        <Button onClick={onReview} className="rounded-full h-10 px-4 bg-brand-purple hover:bg-brand-purpleHover text-white" data-testid={`pu-review-${row.id}`}>
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Review & Approve
        </Button>
      </div>
    </div>
  );
}

function ReviewDialog({ row, onClose, onApproved }) {
  const s = row.existing_student || {};
  const [applyName, setApplyName] = useState(true);
  const [applyBirthday, setApplyBirthday] = useState(!!row.birthday && row.birthday !== s.birthday);
  const [applyAddress, setApplyAddress] = useState(true);
  const [applyNotes, setApplyNotes] = useState(!!row.notes_from_parent);
  const [applyGuardians, setApplyGuardians] = useState((row.guardians || []).length > 0);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/students/pending/${row.id}/approve-profile-update`, {
        apply_name: applyName,
        apply_birthday: applyBirthday,
        apply_address: applyAddress,
        apply_notes: applyNotes,
        apply_guardians: applyGuardians,
      });
      onApproved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto" data-testid="pu-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Review Profile Update</DialogTitle>
          <DialogDescription className="text-brand-inkMuted uppercase">{s.full_name || row.full_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <ToggleRow
            testid="apply-name"
            checked={applyName} onChange={setApplyName}
            label="Update name"
            oldVal={s.full_name || "—"}
            newVal={row.full_name || row.name || "—"}
            same={!nonEmptyDifferent(s.full_name || s.name, row.full_name || row.name)}
          />
          <ToggleRow
            testid="apply-birthday"
            checked={applyBirthday} onChange={setApplyBirthday}
            label="Update birthday"
            oldVal={s.birthday || "—"}
            newVal={row.birthday || "—"}
            same={!nonEmptyDifferent(s.birthday, row.birthday)}
          />
          <ToggleRow
            testid="apply-address"
            checked={applyAddress} onChange={setApplyAddress}
            label="Update address"
            oldVal={joinAddr(s)}
            newVal={joinAddr(row)}
            same={!nonEmptyDifferent(joinAddr(s), joinAddr(row))}
          />
          <ToggleRow
            testid="apply-notes"
            checked={applyNotes} onChange={setApplyNotes}
            label="Append note"
            oldVal="(existing notes untouched)"
            newVal={row.notes_from_parent || "—"}
            same={!row.notes_from_parent}
          />
          <ToggleRow
            testid="apply-guardians"
            checked={applyGuardians} onChange={setApplyGuardians}
            label={`Replace guardian list (${(row.guardians || []).length} submitted)`}
            oldVal={`${(s.guardians || []).length} on file`}
            newVal={`${(row.guardians || []).length} submitted`}
            same={(row.guardians || []).length === 0}
          />
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-2xl h-11">Cancel</Button>
          <Button
            type="button" disabled={saving}
            onClick={submit}
            className="rounded-2xl h-11 px-6 bg-brand-purple hover:bg-brand-purpleHover text-white"
            data-testid="pu-approve-submit"
          >
            {saving ? "Applying…" : "Apply update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ checked, onChange, label, oldVal, newVal, same, testid }) {
  return (
    <div className={`flex items-start justify-between gap-3 p-3 rounded-2xl border ${same ? "border-transparent bg-brand-purpleLight/10 opacity-60" : "border-brand-purple/10 bg-white"}`}>
      <div className="min-w-0 flex-1">
        <Label className="font-heading text-sm">{label}</Label>
        {same ? (
          <p className="text-xs text-brand-inkMuted mt-1 italic">No change submitted</p>
        ) : (
          <div className="flex items-center gap-2 text-xs mt-1">
            <span className="text-brand-inkMuted line-through truncate max-w-[45%]">{oldVal || "—"}</span>
            <ArrowRight className="w-3 h-3 text-brand-purple shrink-0" />
            <span className="text-foreground font-medium truncate max-w-[45%]">{newVal || "—"}</span>
          </div>
        )}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={same} data-testid={testid} />
    </div>
  );
}

function nonEmptyDifferent(a, b) {
  const aa = (a || "").trim();
  const bb = (b || "").trim();
  if (!bb) return false;
  return aa !== bb;
}

function joinAddr(x) {
  return [x?.address, x?.city, x?.province].filter(Boolean).join(", ");
}

function diffFields(existing, incoming) {
  const out = [];
  const iName = incoming.full_name || incoming.name || "";
  if (nonEmptyDifferent(existing.full_name || existing.name, iName)) {
    out.push({ label: "Name", oldVal: existing.full_name || existing.name || "—", newVal: iName, icon: <User className="w-3 h-3" /> });
  }
  if (nonEmptyDifferent(existing.birthday, incoming.birthday)) {
    out.push({ label: "Birthday", oldVal: existing.birthday, newVal: incoming.birthday, icon: <Calendar className="w-3 h-3" /> });
  }
  if (nonEmptyDifferent(joinAddr(existing), joinAddr(incoming))) {
    out.push({ label: "Address", oldVal: joinAddr(existing), newVal: joinAddr(incoming), icon: <MapPin className="w-3 h-3" /> });
  }
  if (incoming.notes_from_parent) {
    out.push({ label: "Note", oldVal: "—", newVal: incoming.notes_from_parent, icon: <FileText className="w-3 h-3" /> });
  }
  const existingG = (existing.guardians || []).length;
  const incomingG = (incoming.guardians || []).length;
  if (incomingG > 0 && incomingG !== existingG) {
    out.push({ label: "Guardians", oldVal: `${existingG} on file`, newVal: `${incomingG} submitted`, icon: <User className="w-3 h-3" /> });
  }
  return out;
}
